/**
 * Guidance Renderer Component
 *
 * Renders Sanity Portable Text content with custom components
 * for app links, callouts, and styled blocks.
 */

import { PortableText, type PortableTextReactComponents } from '@portabletext/react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { AppLinkButton } from './app-link-button';
import { Callout } from './callout';
import { urlFor, type SanityBlock, type SanityAppLink, type SanityCallout } from '@/lib/sanity';

interface GuidanceRendererProps {
  content: SanityBlock[];
  className?: string;
}

/**
 * Custom components for Portable Text rendering
 */
const components: Partial<PortableTextReactComponents> = {
  // Block styles
  block: {
    normal: ({ children }) => (
      <p className="text-stone-600 mb-3 leading-relaxed">{children}</p>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-display font-semibold text-stone-800 mt-6 mb-3">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-stone-700 mt-5 mb-2">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-semibold text-stone-700 mt-4 mb-2">{children}</h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary-300 pl-4 italic text-stone-500 my-4">
        {children}
      </blockquote>
    ),
  },

  // List styles
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside text-stone-600 mb-4 space-y-1.5 pl-2">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside text-stone-600 mb-4 space-y-1.5 pl-2">
        {children}
      </ol>
    ),
    checklist: ({ children }) => (
      <ul className="text-stone-600 mb-4 space-y-2">{children}</ul>
    ),
  },

  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
    checklist: ({ children }) => (
      <li className="flex items-start gap-2">
        <span className="mt-1 w-4 h-4 border-2 border-stone-300 rounded" />
        <span>{children}</span>
      </li>
    ),
  },

  // Mark styles (inline)
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-stone-800">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    underline: ({ children }) => <span className="underline">{children}</span>,
    'strike-through': ({ children }) => (
      <span className="line-through text-stone-400">{children}</span>
    ),

    // External link annotation
    link: ({ children, value }) => {
      const href = value?.href || '#';
      const openInNewTab = value?.openInNewTab !== false;

      return (
        <a
          href={href}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          className="text-primary-600 hover:text-primary-700 underline underline-offset-2 inline-flex items-center gap-1"
        >
          {children}
          {openInNewTab && <ExternalLink className="w-3 h-3" />}
        </a>
      );
    },

    // Internal app link annotation
    internalLink: ({ children, value }) => {
      const route = value?.route || '/';

      return (
        <Link
          to={route}
          className="text-primary-600 hover:text-primary-700 underline underline-offset-2 font-medium"
        >
          {children}
        </Link>
      );
    },
  },

  // Custom block types
  types: {
    // App Link Button block
    appLink: ({ value }: { value: SanityAppLink }) => (
      <div className="my-4">
        <AppLinkButton
          label={value.label}
          route={value.route}
          style={value.style}
          icon={value.icon}
        />
      </div>
    ),

    // Callout block
    callout: ({ value }: { value: SanityCallout }) => (
      <Callout type={value.type} title={value.title} content={value.content} />
    ),

    // Image block
    image: ({ value }) => {
      if (!value?.asset) return null;

      const imageUrl = urlFor(value).width(800).auto('format').url();

      return (
        <figure className="my-6">
          <img
            src={imageUrl}
            alt={value.alt || ''}
            className="rounded-lg shadow-sm w-full"
            loading="lazy"
          />
          {value.caption && (
            <figcaption className="text-sm text-stone-500 text-center mt-2">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

/**
 * Renders Sanity Portable Text content with app-aware links and components
 */
export function GuidanceRenderer({ content, className = '' }: GuidanceRendererProps) {
  if (!content || content.length === 0) {
    return null;
  }

  return (
    <div className={`prose prose-stone max-w-none ${className}`}>
      <PortableText value={content} components={components} />
    </div>
  );
}

